import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Save, Globe, Eye, EyeOff, Plus, Trash2 } from 'lucide-react';

const FieldToggle = ({ label, enabled, onToggle, testId }) => (
  <button
    data-testid={testId}
    onClick={onToggle}
    className={`flex items-center gap-1.5 px-2.5 py-1.5 font-mono text-xs transition-colors rounded ${
      enabled
        ? 'bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30'
        : 'bg-white/5 text-white/30 border border-white/10'
    }`}
    title={enabled ? `Hide ${label} on website` : `Show ${label} on website`}
  >
    {enabled ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
    {enabled ? 'Visible' : 'Hidden'}
  </button>
);

const ContactDetailsTab = ({ authAxios }) => {
  const [contactDetails, setContactDetails] = useState({
    email: 'info@aureate-resident.com', phone: '+44 (0) 131 381 1989', mobile: '', website: 'aureate-resident.com',
    address: 'Third Floor, 3 Hill Street, New Town, Edinburgh, EH2 3JP, United Kingdom',
    phone_visible: true, mobile_visible: true, email_visible: true, address_visible: true, website_visible: true
  });
  const [saving, setSaving] = useState(false);
  const [contactEnabled, setContactEnabled] = useState(true);
  const [globalFootprintEnabled, setGlobalFootprintEnabled] = useState(true);
  const [regions, setRegions] = useState([
    { name: 'Africa', enabled: true },
    { name: 'Caribbean', enabled: true },
    { name: 'Gulf', enabled: true },
    { name: 'UK', enabled: true },
    { name: 'US', enabled: true },
  ]);
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [savingFootprint, setSavingFootprint] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const response = await authAxios.get('/admin/settings');
        if (response.data.contact_details) {
          const d = response.data.contact_details;
          setContactDetails({
            email: d.email || 'info@aureate-resident.com',
            phone: d.phone || '+44 (0) 131 381 1989',
            mobile: d.mobile || '',
            website: d.website || 'aureate-resident.com',
            address: d.address || 'Third Floor, 3 Hill Street, New Town, Edinburgh, EH2 3JP, United Kingdom',
            phone_visible: d.phone_visible !== false,
            mobile_visible: d.mobile_visible !== false,
            email_visible: d.email_visible !== false,
            address_visible: d.address_visible !== false,
            website_visible: d.website_visible !== false,
          });
        }
        if (response.data.contact_enabled !== undefined) setContactEnabled(response.data.contact_enabled);
        if (response.data.global_footprint_enabled !== undefined) setGlobalFootprintEnabled(response.data.global_footprint_enabled);
        if (response.data.global_footprint?.regions) setRegions(response.data.global_footprint.regions);
      } catch (error) {
        console.error('Failed to fetch contact details:', error);
      }
    };
    fetch();
  }, [authAxios]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await authAxios.put('/admin/contact-details', contactDetails);
      toast.success('Contact details saved successfully!');
    } catch (error) {
      toast.error('Failed to save contact details');
    } finally {
      setSaving(false);
    }
  };

  const toggleField = (field) => {
    setContactDetails(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleToggleVisibility = async (field, value) => {
    const newContact = field === 'contact_enabled' ? value : contactEnabled;
    const newFootprint = field === 'global_footprint_enabled' ? value : globalFootprintEnabled;

    if (field === 'contact_enabled') setContactEnabled(value);
    else setGlobalFootprintEnabled(value);

    setSavingVisibility(true);
    try {
      await authAxios.put('/admin/section-visibility', {
        contact_enabled: newContact,
        global_footprint_enabled: newFootprint,
      });
      toast.success(`Section ${value ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      toast.error('Failed to update visibility');
      if (field === 'contact_enabled') setContactEnabled(!value);
      else setGlobalFootprintEnabled(!value);
    } finally {
      setSavingVisibility(false);
    }
  };

  const handleSaveFootprint = async () => {
    setSavingFootprint(true);
    try {
      await authAxios.put('/admin/global-footprint', { regions });
      toast.success('Global footprint updated!');
    } catch (error) {
      toast.error('Failed to save global footprint');
    } finally {
      setSavingFootprint(false);
    }
  };

  const addRegion = () => setRegions([...regions, { name: '', enabled: true }]);
  const removeRegion = (index) => setRegions(regions.filter((_, i) => i !== index));
  const updateRegion = (index, field, value) => {
    const updated = [...regions];
    updated[index] = { ...updated[index], [field]: value };
    setRegions(updated);
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="contact-details-tab">
      {/* Section Visibility Toggles */}
      <div className="bg-[#111111] border border-white/5 p-6">
        <h2 className="font-mono font-bold mb-6 flex items-center gap-2">
          <Eye className="w-5 h-5 text-[#D4AF37]" />
          Section Visibility
        </h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-[#0A0A0A] border border-white/5 rounded">
            <div className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-white/40" />
              <div>
                <span className="font-mono text-sm text-white">Contact Section</span>
                <p className="text-white/40 text-xs mt-0.5">Show/hide entire contact section on website</p>
              </div>
            </div>
            <button
              data-testid="toggle-contact-enabled"
              onClick={() => handleToggleVisibility('contact_enabled', !contactEnabled)}
              disabled={savingVisibility}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 ${
                contactEnabled ? 'bg-[#D4AF37]' : 'bg-white/10'
              }`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-200 ${
                contactEnabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
          <div className="flex items-center justify-between p-4 bg-[#0A0A0A] border border-white/5 rounded">
            <div className="flex items-center gap-3">
              <Globe className="w-4 h-4 text-white/40" />
              <div>
                <span className="font-mono text-sm text-white">Global Footprint Section</span>
                <p className="text-white/40 text-xs mt-0.5">Show/hide global footprint on website</p>
              </div>
            </div>
            <button
              data-testid="toggle-global-footprint-enabled"
              onClick={() => handleToggleVisibility('global_footprint_enabled', !globalFootprintEnabled)}
              disabled={savingVisibility}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 ${
                globalFootprintEnabled ? 'bg-[#D4AF37]' : 'bg-white/10'
              }`}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-200 ${
                globalFootprintEnabled ? 'translate-x-6' : 'translate-x-1'
              }`} />
            </button>
          </div>
        </div>
      </div>

      {/* Contact Details Form with field toggles */}
      <div className="bg-[#111111] border border-white/5 p-6">
        <h2 className="font-mono font-bold mb-2 flex items-center gap-2">
          <Phone className="w-5 h-5 text-[#D4AF37]" />
          Contact Details
        </h2>
        <p className="text-white/40 text-xs font-mono mb-6">Toggle visibility of each field on the public website</p>
        <div className="space-y-4">
          {/* Email */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-white/50 text-xs font-mono uppercase">Email Address</label>
              <FieldToggle label="email" enabled={contactDetails.email_visible} onToggle={() => toggleField('email_visible')} testId="toggle-email-visible" />
            </div>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <input type="email" value={contactDetails.email} onChange={(e) => setContactDetails({...contactDetails, email: e.target.value})} className="w-full bg-[#0A0A0A] border border-white/10 pl-10 pr-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
            </div>
          </div>
          {/* Phone */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-white/50 text-xs font-mono uppercase">Phone Number (Landline)</label>
              <FieldToggle label="phone" enabled={contactDetails.phone_visible} onToggle={() => toggleField('phone_visible')} testId="toggle-phone-visible" />
            </div>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <input type="tel" value={contactDetails.phone} onChange={(e) => setContactDetails({...contactDetails, phone: e.target.value})} placeholder="+44 (0) 131 381 1989" className="w-full bg-[#0A0A0A] border border-white/10 pl-10 pr-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
            </div>
          </div>
          {/* Mobile */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-white/50 text-xs font-mono uppercase">Mobile Number</label>
              <FieldToggle label="mobile" enabled={contactDetails.mobile_visible} onToggle={() => toggleField('mobile_visible')} testId="toggle-mobile-visible" />
            </div>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <input type="tel" value={contactDetails.mobile} onChange={(e) => setContactDetails({...contactDetails, mobile: e.target.value})} placeholder="+44 7XXX XXXXXX" className="w-full bg-[#0A0A0A] border border-white/10 pl-10 pr-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
            </div>
          </div>
          {/* Website */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-white/50 text-xs font-mono uppercase">Website</label>
              <FieldToggle label="website" enabled={contactDetails.website_visible} onToggle={() => toggleField('website_visible')} testId="toggle-website-visible" />
            </div>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              <input type="text" value={contactDetails.website} onChange={(e) => setContactDetails({...contactDetails, website: e.target.value})} placeholder="aureate-resident.com" className="w-full bg-[#0A0A0A] border border-white/10 pl-10 pr-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none" />
            </div>
          </div>
          {/* Address */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-white/50 text-xs font-mono uppercase">Address</label>
              <FieldToggle label="address" enabled={contactDetails.address_visible} onToggle={() => toggleField('address_visible')} testId="toggle-address-visible" />
            </div>
            <div className="relative">
              <MapPin className="absolute left-3 top-3 w-4 h-4 text-white/30" />
              <textarea value={contactDetails.address} onChange={(e) => setContactDetails({...contactDetails, address: e.target.value})} rows={3} className="w-full bg-[#0A0A0A] border border-white/10 pl-10 pr-4 py-3 font-mono text-sm focus:border-[#D4AF37] outline-none resize-none" />
            </div>
          </div>
          <button onClick={handleSave} disabled={saving} data-testid="save-contact-details" className="flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black font-mono text-sm hover:bg-[#D4AF37]/90 transition-colors disabled:opacity-50">
            <Save className="w-4 h-4" />{saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Global Footprint Editor */}
      <div className="bg-[#111111] border border-white/5 p-6">
        <h2 className="font-mono font-bold mb-6 flex items-center gap-2">
          <Globe className="w-5 h-5 text-[#D4AF37]" />
          Global Footprint Regions
        </h2>
        <div className="space-y-3">
          {regions.map((region, index) => (
            <div key={index} className="flex items-center gap-3 p-3 bg-[#0A0A0A] border border-white/5 rounded">
              <input
                type="text"
                value={region.name}
                onChange={(e) => updateRegion(index, 'name', e.target.value)}
                placeholder="Region name"
                data-testid={`footprint-region-input-${index}`}
                className="flex-1 bg-transparent border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none"
              />
              <button
                onClick={() => updateRegion(index, 'enabled', !region.enabled)}
                data-testid={`footprint-region-toggle-${index}`}
                className={`px-3 py-2 font-mono text-xs transition-colors flex items-center gap-1.5 ${
                  region.enabled
                    ? 'bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30'
                    : 'bg-white/5 text-white/40 border border-white/10'
                }`}
              >
                {region.enabled ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                {region.enabled ? 'Visible' : 'Hidden'}
              </button>
              <button
                onClick={() => removeRegion(index)}
                data-testid={`footprint-region-remove-${index}`}
                className="p-2 text-red-400/60 hover:text-red-400 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button
            onClick={addRegion}
            data-testid="add-footprint-region"
            className="flex items-center gap-2 px-4 py-2.5 border border-dashed border-white/10 text-white/50 hover:text-white hover:border-white/30 transition-colors font-mono text-xs w-full justify-center"
          >
            <Plus className="w-4 h-4" />
            Add Region
          </button>
          <button onClick={handleSaveFootprint} disabled={savingFootprint} data-testid="save-global-footprint" className="flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black font-mono text-sm hover:bg-[#D4AF37]/90 transition-colors disabled:opacity-50 mt-2">
            <Save className="w-4 h-4" />{savingFootprint ? 'Saving...' : 'Save Footprint'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactDetailsTab;
